import React, { useState } from 'react';
import {
    TextField, Button, Typography, Stack, Alert,
    Divider, Checkbox, Select, MenuItem, FormControl,
    InputLabel, Grid, Paper
} from '@mui/material';
import { RichTextEditor } from '@mantine/rte';

import api from "../axios/axiosInstance";

const BlogSubmit = ({ blogs, refreshBlogs }) => {
    const [topic, setTopic] = useState('');
    const [subTopic, setSubTopic] = useState('');
    const [content, setContent] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [isUpdate, setIsUpdate] = useState(false);
    const [selectedBlogId, setSelectedBlogId] = useState('');

    const handleSelectChange = (id) => {
        const selected = blogs.find((b) => b.id === id);
        if (selected) {
            setSelectedBlogId(id);
            setTopic(selected.topic || '');
            setSubTopic(selected.sub_topic || '');
            setContent(selected.blog_details || '');
        }
    };

    const handleSubmit = async () => {

        const payload = { topic, sub_topic: subTopic, blog_details: content };
        const headers = {
            'Content-Type': 'application/json',
        };

        try {
            if (isUpdate && selectedBlogId) {
                await api.patch(
                    `${process.env.REACT_APP_BACKEND_URL}/grades/blog-private/${selectedBlogId}/`,
                    payload, { headers }
                );
                setSuccess('Blog updated successfully!');
            } else {
                await api.post(`${process.env.REACT_APP_BACKEND_URL}/grades/blog/`, payload, { headers });
                setSuccess('Blog posted successfully!');
            }

            if (refreshBlogs) refreshBlogs();
            setError('');
            setTopic('');
            setSubTopic('');
            setContent('');
            setSelectedBlogId('');
            setIsUpdate(false);
        } catch (err) {
            console.error('❌ Blog submission error:', err);
            setSuccess('');
            setError('Error submitting blog.');
        }
    };

    return (
        <>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                ✍️ Submit Your Blog
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {success && <Alert severity="success">{success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <Stack spacing={3} mt={2}>
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        backgroundColor: '#EEF2E3',
                        border: '1px solid #C9D5B5',
                        borderRadius: 2,
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Checkbox
                                checked={isUpdate}
                                onChange={(e) => {
                                    setIsUpdate(e.target.checked);
                                    if (!e.target.checked) {
                                        setSelectedBlogId('');
                                        setTopic('');
                                        setSubTopic('');
                                        setContent('');
                                    }
                                }}
                                color="success"
                            />
                            <Typography fontWeight="bold" display="inline" sx={{ ml: 1 }}>
                                Update Existing Blog
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormControl fullWidth disabled={!isUpdate}>
                                <InputLabel>Select a Blog</InputLabel>
                                <Select
                                    value={selectedBlogId}
                                    label="Select a Blog"
                                    onChange={(e) => handleSelectChange(e.target.value)}
                                >
                                    {blogs.map((blog) => (
                                        <MenuItem key={blog.id} value={blog.id}>
                                            {blog.topic}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                <TextField
                    label="Blog Title"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Sub Topic (Optional)"
                    value={subTopic}
                    onChange={(e) => setSubTopic(e.target.value)}
                    fullWidth
                />

                <div>
                    <Typography variant="subtitle1" gutterBottom>
                        Blog Content
                    </Typography>
                    <RichTextEditor
                        value={content}
                        onChange={setContent}
                        styles={{
                            root: {
                                backgroundColor: 'white',
                                borderRadius: 4,
                                border: '1px solid #ccc',
                                minHeight: '300px',
                                paddingBottom: '1rem',
                                overflow: 'auto',
                            },
                            content: {
                                minHeight: '200px',
                                padding: '12px',
                                fontSize: '1rem',
                                lineHeight: 1.6,
                            },
                        }}
                    />
                </div>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="large"
                    sx={{
                        width: { xs: '100%', sm: '220px' },
                        alignSelf: { xs: 'stretch', sm: 'center' },
                        bgcolor: '#6E8B3D',
                        '&:hover': {
                            bgcolor: '#5f7735',
                        },
                    }}
                >
                    {isUpdate ? 'Update Blog' : 'Submit Blog'}
                </Button>
            </Stack>
        </>
    );
};

export default BlogSubmit;
